import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

import { uiReducer } from '../store/reducers/ui.reducer';
import { store } from '../store/store';

const { setIsRedirecting, setShowPaywall } = uiReducer.actions;

interface RetryConfig extends InternalAxiosRequestConfig {
  retryCount?: number;
}

interface ErrorResponseData {
  message?: string;
}

const API_URL =
  import.meta.env.MODE === 'production'
    ? window._env_.VITE_API_URL // Use the base URL from environment variables
    : '/api'; // Use relative path for development

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// Simple in-memory queue to serialize non-GET requests
let nonGetQueue: Promise<unknown> = Promise.resolve();

function enqueueNonGet<T>(task: () => Promise<T>): Promise<T> {
  // Chain the task onto the current queue; ensure the chain continues even if a task fails
  const run = nonGetQueue.then(task, task);
  // Keep the queue alive regardless of success/failure of the current task
  nonGetQueue = run.catch(() => {});
  return run;
}

// Deduplicate concurrent token refresh requests so multiple 401s don't race.
let refreshPromise: Promise<void> | null = null;

export function refreshAccessToken(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = axios({
      method: 'POST',
      url: `${API_URL}/auth/refresh-token`,
      withCredentials: true,
    })
      .then(() => {})
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    // Handle any errors that occur during the request configuration phase
    // Reject the promise with the error object to propagate it
    return Promise.reject(error);
  },
);

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // If the response is successful, just return it
    return response;
  },
  async (error) => {
    // Handle errors
    if (error.response) {
      const status = error.response.status;
      if (window.location.pathname !== '/login' && status === 401) {
        try {
          await refreshAccessToken();

          // If refresh is successful, retry the original request
          const originalRequest = error.config;
          return axiosInstance(originalRequest);
        } catch (_refreshError) {
          // If refresh fails, redirect to login page
          store.dispatch(setIsRedirecting(true));
          const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
          document.cookie = `redirect_url=${currentUrl}; path=/`;
          window.location.href = `/login?returnUrl=${currentUrl}`; // Redirect to login page
        }
      } else if (status === 402) {
        store.dispatch(setShowPaywall(true));
      } else if (status === 403) {
        store.dispatch(setIsRedirecting(true));
        window.location.href = '/forbidden'; // Redirect to forbidden page
      } else if (status >= 500 && status < 600 && error.config?.method?.toUpperCase() === 'GET') {
        return handleRetryLogic(error);
      }
    }
    return Promise.reject(error); // Re-throw the error for further handling
  },
);

const httpRequest = (method: string, url: string, config = {}, accept = 'application/json') => {
  const headers = {
    Accept: accept,
  };
  const requestObject = {
    ...config,
    headers,
    method,
    url,
    withCredentials: true,
  };
  // Queue non-GET requests to avoid parallel execution
  if (method.toUpperCase() !== 'GET' && method.toUpperCase() !== 'OPTIONS' && method.toUpperCase() !== 'HEAD') {
    return enqueueNonGet(() => axiosInstance.request(requestObject));
  }
  return axiosInstance.request(requestObject);
};

export function httpGET(url: string) {
  return httpRequest('GET', url);
}

export function httpGETBlob(url: string): Promise<Blob> {
  return httpRequest('GET', url, { responseType: 'blob' }, 'application/octet-stream').then(
    (res: AxiosResponse<Blob>) => res.data,
  );
}

export function httpPOST(url: string, data: object, config = {}) {
  return httpRequest('POST', url, { data, ...config });
}

export function httpPUT(url: string, data: object, config = {}) {
  return httpRequest('PUT', url, { data, ...config });
}

export function httpPATCH(url: string, data?: object, config = {}) {
  return httpRequest('PATCH', url, { data, ...config });
}

export function httpDELETE(url: string, data: object) {
  return httpRequest('DELETE', url, { data });
}

async function handleRetryLogic(error: AxiosError): Promise<AxiosResponse | void> {
  if (!error.response || !error.config) {
    return;
  }

  const status = error.response.status;
  const originalRequest = error.config as RetryConfig;
  const retryCount = originalRequest.retryCount || 0;
  const maxRetries = 3;

  if (retryCount < maxRetries) {
    originalRequest.retryCount = retryCount + 1;

    // Calculate delay with exponential backoff: 1s, 2s, 4s
    const delay = Math.pow(2, retryCount) * 1000;
    await new Promise((resolve) => setTimeout(resolve, delay));

    return axiosInstance(originalRequest);
  } else {
    store.dispatch(setIsRedirecting(true));
    const errorMessage =
      (error.response?.data as ErrorResponseData)?.message || error.message || 'Service temporarily unavailable';
    const encodedMessage = encodeURIComponent(errorMessage);
    window.location.href = `/service-unavailable?status=${status}&message=${encodedMessage}`;
  }
}
